import { useState } from 'react';
import { useQuery, useMutation } from 'react-apollo';
import pMap from 'p-map-browser';

import { onboardingInvitation as onboardingInvitationMutation } from '../../../../../../../graphql/mutations/onboarding';
import { allPhotosAccessInvitation as allPhotosAccessInvitationMutation } from '../../../../../../../graphql/mutations/all-photos-access';
import { getErrorString } from '../../../../../../../util/normalize-errors';
import {
  AlbumFolderForContributorShare,
  AlbumForContributorShare,
} from '../../../../../../../graphql/queries/contributors';
import { isAlbumIndividual } from '../../../../../../../util/album';
import { MeQuery } from '../../../../../../../graphql/queries/account';
import {
  isAlbumFolderOwner,
  isAlbumOwner,
} from '../../../../../../../util/account';

const useProShare = ({ folderId, albumId }) => {
  const [submitting, setSubmitting] = useState(false);
  const { data: { me: user } = {} } = useQuery(MeQuery);
  const { data: { album, loading: loadingAlbum } = {} } = useQuery(
    AlbumForContributorShare,
    {
      variables: {
        id: albumId,
      },
      skip: !albumId,
    },
  );
  const { data: { albumFolder, loading: loadingAlbumFolder } = {} } = useQuery(
    AlbumFolderForContributorShare,
    {
      variables: {
        id: folderId ?? album?.root_album_folder?.id,
      },
      skip: !folderId && !album?.root_album_folder?.id,
    },
  );

  const entity = isAlbumIndividual(album) ? album : albumFolder;
  const entityIsAlbum = entity === album;

  const [onboardingInvitation] = useMutation(onboardingInvitationMutation);
  const [allPhotosAccessInvitation] = useMutation(
    allPhotosAccessInvitationMutation,
  );

  const onSendOnboardingInvitations = async (invitees, input, inputIsPhone) => {
    if (submitting) {
      return ['Already submitting'];
    }

    setSubmitting(true);
    const errors = [];
    if (invitees.length > 0) {
      await pMap(
        [
          ...invitees,
          input
            ? {
                email: !inputIsPhone ? input : undefined,
                phoneNumber: inputIsPhone ? input : undefined,
              }
            : undefined,
        ].filter(Boolean),
        async invitee => {
          try {
            await onboardingInvitation({
              variables: {
                input: {
                  email_address: invitee.email,
                  phone_number: invitee.phoneNumber,
                  album_id: entityIsAlbum ? entity.id : undefined,
                  album_folder_id: !entityIsAlbum ? entity.id : undefined,
                },
              },
            });
          } catch (err) {
            errors.push(
              `Failed to invite: ${invitee.id}. ${getErrorString(err)}`,
            );
          }
        },
      );
    }
    setSubmitting(false);
    return errors;
  };

  const onSendAllPhotosAccessInvitations = async (
    invitees,
    input,
    inputIsPhone,
  ) => {
    if (submitting) {
      return ['Already submitting'];
    }

    setSubmitting(true);
    const errors = [];
    if (invitees.length > 0) {
      await pMap(
        [
          ...invitees,
          input
            ? {
                email: !inputIsPhone ? input : undefined,
                phoneNumber: inputIsPhone ? input : undefined,
              }
            : undefined,
        ].filter(Boolean),
        async invitee => {
          try {
            await allPhotosAccessInvitation({
              variables: {
                input: {
                  email_address: invitee.email,
                  phone_number: invitee.phoneNumber,
                  album_id: entityIsAlbum ? entity.id : undefined,
                  album_folder_id: !entityIsAlbum ? entity.id : undefined,
                },
              },
            });
          } catch (err) {
            errors.push(
              `Failed to invite: ${
                invitee.email ?? invitee.phoneNumber
              }. ${getErrorString(err)}`,
            );
          }
        },
      );
    }
    setSubmitting(false);
    return errors;
  };

  const onQRCodeDownload = () => {
    const canvas = document.getElementById(`${entity.id}-qr`);
    const pngUrl = canvas
      .toDataURL('image/png')
      .replace('image/png', 'image/octet-stream');
    const downloadLink = document.createElement('a');
    downloadLink.href = pngUrl;
    downloadLink.download = `${entity.id}-QR.png`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  return {
    entity,
    entityIsAlbum,
    isOwner: entityIsAlbum
      ? isAlbumOwner(user, entity)
      : isAlbumFolderOwner(user, entity),
    loading: loadingAlbum || loadingAlbumFolder,
    submitting,
    onSendOnboardingInvitations,
    onSendAllPhotosAccessInvitations,
    onQRCodeDownload,
  };
};

export default useProShare;
